import { inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';

// Enum
import { TypeMessage } from '../common/enums/general/TypeMessage';

// Service
import { UserInteractiveService } from '../Services/user-interactive.service';
import { AuthService } from '../Services/auth.service';
import { LoadingService } from '../shared/components/general/loading/service/loading.service'; // Importar LoadingService
import {
  ICustomErrorResponse,
  IServerErrorResponse,
} from '@/interface/general/IError';

type BackendValidationError = {
  propertyName?: string;
  errorMessage?: string;
  PropertyName?: string;
  ErrorMessage?: string;
};


export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);
  const userInteractiveService = inject(UserInteractiveService);
  const loadingService = inject(LoadingService); // Inyectar LoadingService

  return next(req).pipe(
   catchError((error: HttpErrorResponse) => {
      loadingService.hide(); // Asegurar que el loading se oculte en caso de error
      const serverError = error.error as IServerErrorResponse;
      const messageError = _handleError(error);
       _showMessage(userInteractiveService, messageError);
      _actionError(authService, error);

        const customError: ICustomErrorResponse = {
          originalError: serverError,
          message: messageError,
          status: error.status,
          statusText: error.statusText,
          timestamp: new Date().toISOString(),
          url: req.url,
          validationErrors: serverError?.validationErrors ?? [],
        };

      if (customError.validationErrors?.length) {
          _showValidationErrors(
            userInteractiveService,
            customError.validationErrors
          );
        } else {
          _showMessage(userInteractiveService, messageError);
        }
        _actionError(authService, error);
        return throwError(() => customError);
      })
    );
  };

  function _handleError(_httpErrorResponse: HttpErrorResponse): string {
    let errorMessage = 'Error de conexión al servidor';

    const serverError = _httpErrorResponse.error as Partial<IServerErrorResponse>;
    const mensaje_ = serverError?.mensaje;

    try {
      if (_httpErrorResponse.error instanceof ErrorEvent) {
        // Error del lado del cliente (red, etc.)
        errorMessage = mensaje_ || 'Error de conexión';
      } else {
        // Error del lado del servidor

        // Intentar extraer mensaje del servidor con múltiples fallbacks
        errorMessage =
          _extractServerMessage(serverError) ||
          _getDefaultErrorMessage(_httpErrorResponse.status);

        // Sobrescribir con mensajes específicos por código de estado
        const statusSpecificMessage = _getStatusSpecificMessage(_httpErrorResponse.status);
        if (statusSpecificMessage) {
          errorMessage = mensaje_ ? `${statusSpecificMessage} | ${mensaje_}` : statusSpecificMessage;
        }
      }
    } catch (processingError) {
      errorMessage = 'Error inesperado al procesar la respuesta del servidor';
    }

    return errorMessage;
  }

  function _showValidationErrors(
    userInteractiveService: UserInteractiveService,
    validationErrors: BackendValidationError[]
  ): void {
    if (!validationErrors?.length) return;

    const messages = validationErrors
      .map(err => {
        const field =
          err.propertyName ??
          err.PropertyName ??
          'Campo';

        const message =
          err.errorMessage ??
          err.ErrorMessage ??
          'Error de validación';

        return `• ${field}: ${message}`;
      })
      .join('\n');

    userInteractiveService.sendMessage(
      TypeMessage.ERROR,
      'Errores de validación',
      0,
      messages,
      true,
      true
    );
  }

  function _extractServerMessage(
    serverError: IServerErrorResponse | any | null
  ): string | null {
    if (!serverError || typeof serverError !== 'object') {
      return null;
    }

    // Orden de prioridad para extraer mensaje
    return (
      serverError.message ||
      serverError.mensaje ||
      serverError.errorMensaje ||
      serverError.error ||
      serverError.details ||
      null
    );
  }

function _getStatusSpecificMessage(status: number): string | null {
  switch (status) {
    case 401:
      return 'Sin autorización para acceder a este contenido';
    case 403:
      return 'No tiene permisos para realizar esta acción';
    case 404:
      return 'Recurso no encontrado';
    case 500:
      return 'Error interno del servidor';
    case 502:
      return 'Error de comunicación con el servidor';
    case 503:
      return 'Servicio no disponible temporalmente';
    case 504:
      return 'Tiempo de espera agotado';
    case 0:
      return 'Error de conexión. Verifique su conexión a internet';
    default:
      return null;
  }
}

// Type guard para verificar si es un error personalizado
export function isCustomErrorResponse(
  error: any
): error is ICustomErrorResponse {
  return (
    error &&
    typeof error.message === 'string' &&
    typeof error.status === 'number' &&
    typeof error.timestamp === 'string'
  );
}

function _getDefaultErrorMessage(status: number): string {
  if (status >= 500) {
    return 'Error del servidor. Por favor, intente más tarde';
  } else if (status >= 400) {
    return 'Error en la solicitud. Verifique los datos enviados';
  } else {
    return 'Error de conexión al servidor';
  }
}

function _actionError(
  authService: AuthService,
  error: HttpErrorResponse
): void {
  switch (error.status) {
    case 401:
    case 403:
      authService.goUnauthorized();
      break;
    case 0:
      // Error de red - no hacer logout
      console.warn('Error de conexión de red');
      break;
    default:
      // Log para otros errores sin acción específica
      console.warn(`Error HTTP ${error.status}:`, error.message);
      break;
  }
}

function _showMessage(
  userInteractiveService: UserInteractiveService,
  message: string
): void {
  if (message && message.trim()) {
    userInteractiveService.sendMessage(
      TypeMessage.ERROR,
      'Error',
      0,
      message,
      true,
      true
    );
  }
}
